import { where, includes, pipe, prop, propOr, pathEq, hasPath, props, flatten, filter, map, sum } from 'ramda';
import { codes as paymentCodes } from 'constants/payment';
import { bookingStatus } from 'types/booking';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { hasPayout, isBalance, isPayout, hasParentPayment, isOverPaid, convertGDSPayments, getDisplayStatus } from 'utils/booking';
import { GDS_AIR_ORGANIZATION } from '../../../types/constants';

const Booking = {
  displayStatus: ({ status, isInAfterSales, outbound, inbound, totalPriceToBePaid, latestRevisionSource, cancelledTimestamp }) =>
    getDisplayStatus({ status, isInAfterSales, totalPriceToBePaid, outbound, inbound, latestRevisionSource, cancelledTimestamp }),
  arePassengersEditable: ({ status, isInAfterSales, outbound, inbound, totalPriceToBePaid }) =>
    !isInAfterSales &&
    [bookingStatus.CONFIRMED, bookingStatus.TEMPORARILY_ON_HOLD, bookingStatus.ON_HOLD].includes(status) &&
    !isOverPaid(status, totalPriceToBePaid) &&
    flatten([...outbound, ...inbound].map(({ products }) => products)).some(({ canChangeName }) => canChangeName),
  allBookingItems: ({ inbound = [], outbound = [] }) =>
    [...outbound, ...inbound].flatMap(({ products }) => products).map((product) => ({ ...product, __typename: 'BookingItem' })),
  eurostarBookingItems: pipe(
    props(['outbound', 'inbound']),
    flatten,
    filter(pathEq(['serviceType', 'code'], serviceTypeCodes.EUROSTAR)),
    map(prop('products')),
    flatten,
    filter((p) => (hasPath(['isCancelled'], p) ? !prop('isCancelled', p) : true)),
    map((product) => ({ ...product, __typename: 'BookingItem' }))
  ),
  greenspeedBookingItems: pipe(
    props(['outbound', 'inbound']),
    flatten,
    filter(pathEq(['serviceType', 'code'], serviceTypeCodes.GREENSPEED)),
    map(prop('products')),
    flatten,
    filter((p) => (hasPath(['isCancelled'], p) ? !prop('isCancelled', p) : true)),
    map((product) => ({ ...product, __typename: 'BookingItem' }))
  ),
  totalExchangeFee: pipe(
    prop('pendingCancellationLegs'),
    map(propOr([], 'fees')),
    flatten,
    filter((fee) => fee.provisional && !fee.cancelled),
    map(prop('price')),
    sum
  ),
  totalPendingCreationFare: pipe(
    prop('pendingCreationLegs'),
    map(prop('bookingItems')),
    flatten,
    filter((fee) => fee.provisional && !fee.cancelled),
    map(prop('price')),
    sum
  ),
  totalPendingCancellationFare: pipe(
    prop('pendingCancellationLegs'),
    map(prop('bookingItems')),
    flatten,
    filter((fee) => fee.provisional && fee.cancelled),
    map(prop('price')),
    sum
  ),
  eurostarOutbound: pipe(propOr([], 'outbound'), filter(pathEq(['serviceType', 'code'], 'ES'))),
  eurostarInbound: pipe(propOr([], 'inbound'), filter(pathEq(['serviceType', 'code'], 'ES'))),
  greenspeedOutbound: pipe(propOr([], 'outbound'), filter(pathEq(['serviceType', 'code'], 'ER'))),
  greenspeedInbound: pipe(propOr([], 'inbound'), filter(pathEq(['serviceType', 'code'], 'ER'))),
  payments: ({ payments = [], agent = {} }) => {
    const paymentList = agent.organization === GDS_AIR_ORGANIZATION ? convertGDSPayments(payments) : payments;
    return paymentList.map((p) => ({
      ...p,
      action: isPayout(p.reference) || p.method === paymentCodes.REFUND ? 'Refund' : 'Payment',
      display: (() => {
        if (hasPayout(p.refId, paymentList)) {
          return false;
        }
        if (isBalance(p.reference)) {
          return false;
        }
        if (isPayout(p.reference) && !hasParentPayment(p.reference, paymentList)) {
          return true;
        }

        return true;
      })()
    }));
  },
  refunds: pipe(
    propOr([], 'payments'),
    filter(
      where({
        reference: (ref) => ref && includes('payout', ref)
      })
    )
  )
};

export default Booking;
